import { Box, Button, LinearProgress, Link } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EntitlementSearchForm } from '../../component/EntitlementSearchForm/EntitlementSearchForm'
import { fetchEntitlementList } from '../../service/Service'
import { AppContext } from '../../store/AppProvider'
import { SHOW_ERROR_ALERT, SHOW_WARNING_ALERT } from '../../store/reducer/AlertNotifierReducer'
import { accessibility, FEATURES, isEmptyObj, scrollToElement } from '../../utils/Utils'
import { ArrowDownward, FileDownload } from '@mui/icons-material'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { ErrorConstant, Warnings } from '../../utils/Constants'


export const EntitlementSearch = () => {
    const { setAlert, entitlementSearchParams, activeRole } = useContext(AppContext)
    const [showTable, setShowTable] = useState(false)
    const [showLoading, setShowLoading] = useState(false)
    const [tableData, setTableData] = useState([])
    const [totalRecordCount, setTotalRecordCount] = useState(0)
    const navigate = useNavigate()
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 25,
    });

    const onSearch = useCallback(async (formData) => {
        try {
            setShowLoading(true)
            const fetchedData = await fetchEntitlementList({ ...formData, ...pagination })
            if (fetchedData?.dataSizeError) {
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: fetchedData?.dataSizeError });
                setShowLoading(false);
                setTableData({});
                return;
            }
            setTableData(fetchedData?.data)
            setTotalRecordCount(fetchedData?.totalCount)
            scrollToElement('search_table')
            setShowTable(true)
        } catch (error) {
            if (error?.code === 'ERR_NETWORK') {
                setAlert({ type: SHOW_WARNING_ALERT, alertMessage: Warnings.optimizeSearch })
            } else {
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
            }
        } finally {
            setShowLoading(false)
        }
    }, [pagination, setAlert])

    const navigateToDetailsPage = useCallback(({ registration_id }) => {
        navigate(`${registration_id}/details`, { state: { registrationId: registration_id } })
    }, [navigate])


    const columns = useMemo(
        () => [
            {
                accessorKey: 'registration_id',
                header: 'Registration ID',
                size: 100,
                Cell: ({ row: { original } }) => {
                    return accessibility(FEATURES.VIEW_REGISTRATIONS_DETAILS, activeRole)
                        ? <Link component="button" onClick={() => navigateToDetailsPage(original)}>
                            {original?.registration_id}
                        </Link>
                        : original?.registration_id
                }
            },
            {
                accessorKey: 'sku_name',
                header: 'SKU',
                size: 150,
            },
            {
                accessorKey: 'purchase_plan_name', //access nested data with dot notation
                header: 'Plan Name',
                size: 150,
            },
            {
                accessorKey: 'purchase_plan_description', //access nested data with dot notation
                header: 'Plan Description',
                size: 150,
            },
            {
                accessorKey: 'planned_purchase_price',
                header: 'Planned Price',
                size: 150,

            },
            {
                accessorKey: 'price_paid',
                header: 'Paid Price',
                size: 150,
            },
            {
                accessorFn: (row) => new Date(row.purchase_date),
                header: 'Purchase Date',
                Cell: ({ cell }) => cell.getValue()?.toLocaleDateString(),
                size: 150,
            },
            {
                accessorFn: (row) => new Date(row.purchase_start_date),
                header: 'Start Date',
                size: 150,
                Cell: ({ cell }) => cell.getValue()?.toLocaleDateString(),
            },
            {
                accessorFn: (row) => new Date(row.purchase_end_date),
                header: 'End Date',
                size: 150,
                Cell: ({ cell }) => cell.getValue()?.toLocaleDateString(),
            },

            {
                accessorKey: 'email',
                header: 'Email',
                size: 210,
            },
        ],
        [navigateToDetailsPage, activeRole],
    );

    const handleExportData = async (columns) => {
        try {
            const fetchedData = await fetchEntitlementList({ ...entitlementSearchParams, ...pagination, isDownload: true })
            window.open(fetchedData, "_blank");
        } catch (e) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
        }
    }

    useEffect(() => {
        if (!isEmptyObj(entitlementSearchParams)) {
            onSearch(entitlementSearchParams)
        }
    }, [pagination, entitlementSearchParams, onSearch])


    const table = useMaterialReactTable({
        columns,
        data: tableData,
        enableStickyHeader: true,
        positionGlobalFilter: 'right',
        // manualFiltering: false,
        enableGlobalFilter: false,
        // onGlobalFilterChange: setGlobalFilter,
        showHideFilters: false,
        // enableTopToolbar: true,
        enableColumnFilter: false,
        enableHiding: true, //this shows the hide/ show columns
        // enableFullScreenToggle: true,
        enableColumnActions: false,
        enableDensityToggle: false,
        //  enableFilters: false, //this shows the search icon and filter icon together
        enableColumnFilters: false, //this hides the column filter
        muiSearchTextFieldProps: {
            placeholder: `Search by hardware SKU`,
            sx: { minWidth: '300px' },
            variant: 'outlined',
        },
        muiTableContainerProps: { sx: { maxHeight: '400px' } },
        initialState: {
            // columnVisibility: { action: false },
            columnPinning: { left: ['registration_id'], right: ['action'] },
            density: 'comfortable',
        },
        muiTableHeadCellProps: {
            align: 'center',
            //simple styling with the `sx` prop, works just like a style prop in this example
            sx: {
                backgroundColor: '#1976d2',
                color: 'white',
                fontSize: '14px',
                verticalAlign: 'middle',
            },
        },
        muiColumnActionsButtonProps: ({ column, table }) => (<ArrowDownward />),
        muiTableBodyCellProps: {
            align: 'center',
            sx: {
                borderRight: '1px solid #e0e0e0',
                verticalAlign: 'middle',
            }
        },

        renderTopToolbarCustomActions: ({ table }) => (
            <Box
                sx={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
                    onClick={() => handleExportData(table?.getVisibleFlatColumns())}
                    startIcon={<FileDownload />}
                >
                    Export CSV
                </Button>
            </Box>
        ),
        manualPagination: true,
        onPaginationChange: setPagination,
        paginationDisplayMode: 'pages',
        rowCount: totalRecordCount, //should be the total records coming from response
        muiPaginationProps: {
            showRowsPerPage: false,
            shape: 'rounded',
            showFirstButton: true,
            showLastButton: true,
        },
        state: {
            pagination,
            // globalFilter
        },

    });

    return (
        <div>
            <div className='header-title' style={{ letterSpacing: '0.5px' }}>{('Entitlement Search').toUpperCase()}</div>
            <EntitlementSearchForm onSearch={(formData) => onSearch(formData)} loading={showLoading} />
            <div id={"search_table"} style={{ marginTop: 25 }}>
                {showLoading && <LinearProgress />}
                {showTable &&
                    <MaterialReactTable
                        table={table}
                    />}
            </div>
        </div>
    )
}
